
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { blogPosts } from "@/data/blogPosts";
import { useState } from "react";
import { CATEGORY_ICONS } from "@/utils/image/constants";
import { Clock, Briefcase, DollarSign, Megaphone, Code, Palette, FileText } from "lucide-react";

function RecentPostItem({ slug, title, publishedAt, category }: { 
  slug: string; 
  title: string; 
  publishedAt: string; 
  category: string;
}) {
  // Get appropriate icon for this category
  const categoryIcon = CATEGORY_ICONS[category] || CATEGORY_ICONS['default'];
  
  return (
    <div className="flex gap-3">
      <Link to={`/blog/${slug}`} className="shrink-0 overflow-hidden rounded-md">
        <div className="w-16 h-16 bg-muted/30 flex items-center justify-center text-primary">
          {/* Render dynamic icon based on category */}
          {categoryIcon === 'clock' && <Clock size={24} />}
          {categoryIcon === 'briefcase' && <Briefcase size={24} />}
          {categoryIcon === 'dollar-sign' && <DollarSign size={24} />}
          {categoryIcon === 'megaphone' && <Megaphone size={24} />}
          {categoryIcon === 'code' && <Code size={24} />}
          {categoryIcon === 'palette' && <Palette size={24} />}
          {categoryIcon === 'file-text' && <FileText size={24} />}
        </div>
      </Link>
      <div>
        <Link to={`/blog/${slug}`} className="font-medium hover:text-primary line-clamp-2 transition-colors">
          {title}
        </Link>
        <p className="text-xs text-muted-foreground mt-1">
          {new Date(publishedAt).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          })}
        </p>
      </div>
    </div>
  );
}

export function BlogSidebar() {
  const [email, setEmail] = useState("");
  
  // Get most recent posts (top 4)
  const recentPosts = [...blogPosts]
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
    .slice(0, 4);
  
  // Get unique categories with count
  const categories = Object.entries(
    blogPosts.reduce((acc, post) => {
      acc[post.category] = (acc[post.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>)
  ).sort((a, b) => b[1] - a[1]);
  
  // Get all tags with count
  const tags = Object.entries(
    blogPosts.reduce((acc, post) => {
      post.tags.forEach(tag => {
        acc[tag] = (acc[tag] || 0) + 1;
      });
      return acc;
    }, {} as Record<string, number>)
  ).sort((a, b) => b[1] - a[1]);
  
  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`Thank you for subscribing! We'll send updates to ${email}`);
    setEmail("");
  };
  
  return (
    <div className="space-y-8">
      {/* Newsletter subscription */}
      <div className="bg-muted/30 rounded-lg p-6 border">
        <h3 className="font-bold text-lg mb-2">Subscribe to Our Newsletter</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Get the latest freelancing tips and resources delivered directly to your inbox.
        </p>
        <form onSubmit={handleSubscribe}>
          <Input 
            type="email" 
            placeholder="Your email address" 
            className="mb-2"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Button type="submit" className="w-full">Subscribe</Button>
        </form>
      </div>
      
      {/* Recent posts */}
      <div className="animate-fade-in">
        <h3 className="font-bold text-lg mb-4">Recent Posts</h3>
        <div className="space-y-4">
          {recentPosts.map(post => (
            <RecentPostItem 
              key={post.id}
              slug={post.slug}
              title={post.title}
              publishedAt={post.publishedAt}
              category={post.category}
            />
          ))}
        </div>
      </div>
      
      {/* Categories */}
      <div className="animate-fade-in" style={{animationDelay: "100ms"}}>
        <h3 className="font-bold text-lg mb-4">Categories</h3>
        <div className="space-y-2">
          {categories.map(([category, count]) => (
            <div key={category} className="flex items-center justify-between">
              <Link to={`/blog?category=${category}`} className="hover:text-primary transition-colors flex items-center">
                {category === 'Productivity' && <Clock className="mr-2 h-4 w-4" />}
                {category === 'Business' && <Briefcase className="mr-2 h-4 w-4" />}
                {category === 'Finance' && <DollarSign className="mr-2 h-4 w-4" />}
                {category === 'Marketing' && <Megaphone className="mr-2 h-4 w-4" />}
                {category === 'Technology' && <Code className="mr-2 h-4 w-4" />}
                {category === 'Design' && <Palette className="mr-2 h-4 w-4" />}
                {category}
              </Link>
              <span className="text-sm text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                {count}
              </span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Tags cloud */}
      <div className="animate-fade-in" style={{animationDelay: "200ms"}}>
        <h3 className="font-bold text-lg mb-4">Popular Tags</h3>
        <div className="flex flex-wrap gap-2">
          {tags.slice(0, 12).map(([tag, count]) => (
            <Link 
              key={tag} 
              to={`/blog?tag=${tag}`} 
              className="bg-muted px-3 py-1 rounded-full text-sm text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
            >
              #{tag} <span className="opacity-70">({count})</span>
            </Link>
          ))}
        </div>
      </div>
      
      {/* CTA */}
      <div className="bg-primary/5 rounded-lg p-6 border border-primary/20 animate-fade-in" style={{animationDelay: "300ms"}}>
        <h3 className="font-bold text-lg mb-2">Ready to Get Started?</h3>
        <p className="text-sm mb-4">
          Join thousands of freelancers who use Hour Tracker to manage their time and get paid faster.
        </p>
        <Button asChild>
          <Link to="/auth?mode=signup">Sign Up Free</Link>
        </Button>
      </div>
    </div>
  );
}
